import DepartmentEnum from '../../../../Enums/DepartmentEnum';
import FreelancerAgencyUserTypesEnum from '../../../../Enums/FreelancerAgencyUserTypesEnum';
import InputTypesEnum from '../../../../Enums/InputTypesEnum';
import ValidityHelper from '../../../../Helpers/ValidityHelper';
import EditFreelancerUserHelper from '../EditFreelancerUserHelper';
import GeneralData from './GeneralData';

export default class EmploymentInformation {
  //#region  Validation
  static validityCheckerForCompanyName(e, editedItem) {
    if (editedItem.UserType === FreelancerAgencyUserTypesEnum.Agency) {
      return ValidityHelper.forRequiredTextField(e);
    }
    return '';
  }

  static validityCheckerForFreelancerRoleIds(e, editedItem) {
    if (editedItem.IsFieldworkPartner.toString() !== 'true') {
      return ValidityHelper.forRequiredMultiSelectField(e);
    }
    return '';
  }

  static validityCheckerForPrimaryFreelancerUserId(e, editedItem) {
    if (
      editedItem.UserType === FreelancerAgencyUserTypesEnum.UserWithAgency &&
      ValidityHelper.forRequiredSelectField(e) !== ''
    ) {
      return 'Select an Agency!';
    }
    return '';
  }

  static isUsaOffice(editedItem) {
    const officeId = !editedItem.PaymentOfficeId ? editedItem.OfficeId : editedItem.PaymentOfficeId;
    return officeId != null && officeId.toString() === '1';
  }

  static validityCheckerForEvaluator(e, editedItem) {
    if (EmploymentInformation.isUsaOffice(editedItem) && !editedItem.PitpEvaluatorId) {
      return 'A valid evaluator is required for the USA office.';
    }
    return '';
  }
  //#endregion

  //#region  OnChange
  static onIsFieldworkPartnerChange(editedItem) {
    if (editedItem.IsFieldworkPartner.toString() === 'true') {
      editedItem.FreelancerRoleIds = [];
      editedItem.UserType = EditFreelancerUserHelper.getDefaultAgencyUserType(editedItem);
      EmploymentInformation.onUserTypeChange(editedItem);
    }
  }

  static onUserTypeChange(editedItem) {
    if (editedItem.UserType !== FreelancerAgencyUserTypesEnum.UserWithAgency) {
      editedItem.PrimaryFreelancerUserId = null;
    }

    const officeId = editedItem.PaymentOfficeId ? editedItem.PaymentOfficeId : editedItem.OfficeId;
    if (officeId.toString() === '6') {
      editedItem.PaymentMethod = EditFreelancerUserHelper.getDefaultPaymentMethodValue(editedItem);
      editedItem.PaymentCurrencyId = EditFreelancerUserHelper.getDefaultPaymentCurrencyValue(editedItem);
    }

    GeneralData.UpdateBillingFields(editedItem);
  }

  static onIsEmployeeChange(editedItem) {
    GeneralData.UpdateBillingFields(editedItem);
  }

  static onFreelancerRoleIdsChange(editedItem) {
    if (
      editedItem.FreelancerRoleIds &&
      editedItem.FreelancerRoleIds.length > 0 &&
      editedItem.FreelancerRoleIds.some(
        (roleId) =>
          roleId.toString() === DepartmentEnum.RM.toString() || roleId.toString() === DepartmentEnum.DQ.toString()
      )
    ) {
      editedItem.InvoiceNeeded = EditFreelancerUserHelper.getDefaultInvoiceNeededValue(editedItem);
    }

    if (
      !editedItem.FreelancerRoleIds ||
      !editedItem.FreelancerRoleIds.some((x) => x.toString() === DepartmentEnum.RM.toString())
    ) {
      editedItem.Managers?.forEach((manager) => {
        if (manager.departmentLookupItemValue.toString() === DepartmentEnum.RM.toString()) {
          manager.managerId = null;
        }
      });
    }

    if (
      !editedItem.FreelancerRoleIds ||
      !editedItem.FreelancerRoleIds.some((x) => x.toString() === DepartmentEnum.DQ.toString())
    ) {
      editedItem.Managers?.forEach((manager) => {
        if (manager.departmentLookupItemValue.toString() === DepartmentEnum.DQ.toString()) {
          manager.managerId = null;
        }
      });
    }
  }
  //#endregion

  static returnDescriptionTab() {
    return {
      label: 'Employment Details',
      fields: [
        {
          fieldName: 'IsFieldworkPartner',
          label: 'Fieldwork Partner',
          className: 'col-6',
          type: InputTypesEnum.Bool,
          validityChecker: ValidityHelper.forRequiredBoolField,
          onFieldChange: EmploymentInformation.onIsFieldworkPartnerChange,
        },
        {
          fieldName: 'FreelancerRoleIds',
          label: 'Departments',
          className: 'col-6',
          type: InputTypesEnum.FreelancerDepartmentMultiChoice,
          validityChecker: EmploymentInformation.validityCheckerForFreelancerRoleIds,
          disabled: (item) => item.IsFieldworkPartner.toString() === 'true',
          onFieldChange: EmploymentInformation.onFreelancerRoleIdsChange,
        },
        {
          fieldName: 'CompanyName',
          label: 'Company Name',
          className: 'col-6',
          type: InputTypesEnum.Text,
          validityChecker: EmploymentInformation.validityCheckerForCompanyName,
        },
        {
          fieldName: 'ContractExpiryDate',
          label: 'Contract Expiry Date',
          className: 'col-6',
          type: InputTypesEnum.DateWithIndefinite,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'UserType',
          label: 'User Type',
          className: 'col-6',
          type: (item) => (!item.FreelancerUserId ? InputTypesEnum.Select : InputTypesEnum.TextReadOnly),
          selectvalues: EditFreelancerUserHelper.getSelectOptionsForUserType,
          validityChecker: ValidityHelper.AlwaysFlawless,
          info: "Be careful! You won't be able to modify this field later!",
          disabled: (item) =>
            item.IsFieldworkPartner.toString() !== 'true' &&
            (!item.FreelancerRoleIds || item.FreelancerRoleIds.length === 0),
          onFieldChange: EmploymentInformation.onUserTypeChange,
        },
        {
          fieldName: 'PrimaryFreelancerUserId',
          label: 'Select Agency',
          className: 'col-6',
          type: InputTypesEnum.PrimaryFreelancerUserSelect,
          validityChecker: EmploymentInformation.validityCheckerForPrimaryFreelancerUserId,
          idFilter: 'OfficeId',
          disabled: (item) =>
            item.IsFieldworkPartner.toString() === 'true' ||
            item.UserType !== FreelancerAgencyUserTypesEnum.UserWithAgency,
        },
        {
          fieldName: 'PitpEvaluatorId',
          label: 'TP Evaluator Email (Bravo TP Evaluator ID)',
          className: 'col-12',
          type: InputTypesEnum.TpEvaluatorSelect,
          validityChecker: EmploymentInformation.validityCheckerForEvaluator,
          info: 'Type at least 3 characters of the evaluator email to search.',
        },
        {
          fieldName: 'BareContactUserId',
          label: 'Bare Contact Person',
          className: 'col-12',
          type: InputTypesEnum.ActiveUserSelect,
          validityChecker: ValidityHelper.forRequiredSelectField,
        },
        {
          fieldName: 'PayIfSame',
          label: 'Pay if assignment scheduled for themselves?',
          className: 'col-6',
          type: InputTypesEnum.OnOff,
          validityChecker: ValidityHelper.forRequiredBoolField,
        },
        {
          fieldName: 'TimebasedSettlementEnabled',
          label: 'Time-Based Settlement',
          className: 'col-6',
          type: InputTypesEnum.OnOff,
          validityChecker: ValidityHelper.AlwaysFlawless,
          hide: (item) => item.UserType === FreelancerAgencyUserTypesEnum.UserWithAgency,
        },
        {
          fieldName: 'IsEmployee',
          label: 'Is Employee?',
          className: 'col-6',
          type: InputTypesEnum.OnOff,
          validityChecker: ValidityHelper.AlwaysFlawless,
          hide: (item) => item.UserType === FreelancerAgencyUserTypesEnum.UserWithAgency,
          onFieldChange: EmploymentInformation.onIsEmployeeChange,
        },
        {
          fieldName: 'InvoiceNeeded',
          label: 'Invoice needed?',
          className: 'col-6',
          type: InputTypesEnum.OnOff,
          validityChecker: ValidityHelper.AlwaysFlawless,
          hide: (item) => item.UserType === FreelancerAgencyUserTypesEnum.UserWithAgency,
        },
      ],
    };
  }
}
